import axios from 'axios';
import { fbLogout } from '../Utils/facebook';
import { basename } from "../Constants/env"

export const config = {
  baseURL: '/api/v1',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 10000,
};

const api = axios.create(config);

const secureApi = axios.create(config);
const TOKEN_STORAGE_KEY = 'token';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';
const USER_STORAGE_KEY = 'user';
const TOKEN_ADMIN_STORAGE_KEY = 'token_admin';
const USER_ADMIN_STORAGE_KEY = 'user_admin';

export const getToken = () => localStorage.getItem(TOKEN_STORAGE_KEY);
const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
const setToken = (token) => localStorage.setItem(TOKEN_STORAGE_KEY, token);
export const setLocalUser = (user) => localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
export const saveAuthData = (authResponse) => {
  const { access_token, refresh_token, user } = authResponse;

  localStorage.setItem(TOKEN_STORAGE_KEY, `Bearer ${access_token}`);
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh_token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));

  // Emit a custom event to notify about user data changes
  window.dispatchEvent(new CustomEvent('user-data-change', {
    detail: { type: 'login' }
  }));

  return user;
};

export const saveAuthDataForAdmin = (authResponse) => {
  const { access_token, user } = authResponse;

  localStorage.setItem(TOKEN_ADMIN_STORAGE_KEY, localStorage.getItem(TOKEN_STORAGE_KEY));
  localStorage.setItem(USER_ADMIN_STORAGE_KEY, localStorage.getItem(USER_STORAGE_KEY));
  localStorage.setItem(TOKEN_STORAGE_KEY, `Bearer ${access_token}`);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  return user;
};

export const getBackToAdmin = () => {
  localStorage.setItem(TOKEN_STORAGE_KEY, localStorage.getItem(TOKEN_ADMIN_STORAGE_KEY));
  localStorage.setItem(USER_STORAGE_KEY, localStorage.getItem(USER_ADMIN_STORAGE_KEY));
  localStorage.removeItem(TOKEN_ADMIN_STORAGE_KEY);
  localStorage.removeItem(USER_ADMIN_STORAGE_KEY);
  return JSON.parse(localStorage.getItem(USER_STORAGE_KEY));
};

export const isUserOriginAdmin = () => localStorage.getItem(USER_ADMIN_STORAGE_KEY);
export const clearAuthData = () => {
  // Disconnect socket if available in the window
  if (window.socketService && typeof window.socketService.disconnect === 'function') {
    window.socketService.disconnect();
  }

  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(TOKEN_ADMIN_STORAGE_KEY);
  localStorage.removeItem(USER_ADMIN_STORAGE_KEY);

  // Emit a custom event to notify other components about logout
  window.dispatchEvent(new CustomEvent('user-data-change', {
    detail: { type: 'logout' }
  }));
};

export const getStoredUser = () => {
  const userStr = localStorage.getItem(USER_STORAGE_KEY);
  try {
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    return null;
  }
};
const removeTokens = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const refreshAccessToken = async () => {
  try {
    const refresh_token = getRefreshToken();
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh-token', {
      refresh_token,
    });

    const { access_token } = response.data?.data;

    setToken("Bearer " + access_token);

    // Emit a custom event to notify about token changes
    window.dispatchEvent(new CustomEvent('user-data-change', {
      detail: { type: 'token-refresh' }
    }));

    return "Bearer " + access_token;
  } catch (error) {
    if (error?.response?.status === 401) {
      removeTokens();
    }
    throw error;
  }
};

const checkNotAuthored = async (error) => {
  const originalRequest = error.config;

  // Handle 503 error (Service Unavailable)
  if (error.response && error.response.status === 503) {
    console.error('Server is currently unavailable. Please try again later.');
    // You can also display a more user-friendly message here if needed
    return Promise.reject({
      ...error,
      handled: true,
      message: 'Server is currently unavailable. Please try again later.'
    });
  }

  if (!error.response || error.response.status !== 401 || originalRequest._retry) {
    throw error;
  }

  if (isRefreshing) {
    try {
      const token = await new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
      originalRequest.headers.Authorization = token;
      return secureApi(originalRequest);
    } catch (err) {
      throw err;
    }
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const token = await refreshAccessToken();
    processQueue(null, token);
    originalRequest.headers.Authorization = token;
    return await secureApi(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError, null);
    
    // Only logout if the refresh token request specifically returned 401
    if (refreshError.response && refreshError.response.status === 401) {
      await fbLogout();
      clearAuthData();
      window.location.href = basename + '/login';
    }
    
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};

secureApi.interceptors.request.use(
  async (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

secureApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    try {
      return await checkNotAuthored(error);
    } catch (err) {
      return Promise.reject(err);
    }
  }
);

export const secureGet = async (url, config = {}, params = {}) => {
  const { headers, ...restConfig } = config;
  return secureApi.get(url, { headers: { ...headers }, ...restConfig, params });
};

export const secureDelete = async (url, config = {}) => {
  const { headers, ...restConfig } = config;
  return secureApi.delete(url, { headers: { ...headers }, ...restConfig });
};

export const securePost = async (url, data, config = {}) => {
  const { headers, ...restConfig } = config;
  return secureApi.post(url, data, { headers: { ...headers }, ...restConfig });
};

export const securePatch = async (url, data, config = {}) => {
  const { headers, ...restConfig } = config;
  return secureApi.patch(url, data, { headers: { ...headers }, ...restConfig });
};

export const securePut = async (url, data, config = {}) => {
  const { headers, ...restConfig } = config;
  return secureApi.put(url, data, { headers: { ...headers }, ...restConfig });
};

export const handleApiError = (error) => {
  if (error.response) {
    return {
      status: error.response.status,
      message: error.response.data.message || 'An error occurred',
      data: error.response.data
    };
  } else if (error.request) {
    return {
      status: 0,
      message: 'No response from server',
      data: null
    };
  }
  return {
    status: 0,
    message: error.message || 'Request failed',
    data: null
  };
};

export { api, secureApi };